import {
  type AutoReviewView,
  type Content,
  type ContentStatus,
  type Production,
} from './api.ts';
import { hourMinute, localTime, startsIn } from './schedule.ts';
import * as wording from './wording.ts';

type StageKey = 'plan' | 'ready' | 'produce' | 'review' | 'delivered';

const STAGE_ORDER: readonly StageKey[] = ['plan', 'ready', 'produce', 'review', 'delivered'];

const STAGE_LABEL: Record<StageKey, string> = {
  plan: wording.STAGE_PLAN,
  ready: wording.STAGE_READY,
  produce: wording.STAGE_MAKING,
  review: wording.STAGE_REVIEW,
  delivered: wording.STAGE_DONE,
};

/** How a segment is drawn: behind the content, the one it is in, or ahead. */
type SegmentState = 'done' | 'current' | 'failed' | 'todo';

interface Segment {
  readonly stage: StageKey;
  /** What the stage is called, in the operator's words. */
  readonly label: string;
  readonly state: SegmentState;
  /** How much of the segment is filled, 0 to 100. */
  readonly percent: number;
  readonly detail: string;
  /** The content is waiting on the operator at this stage. */
  readonly needsYou: boolean;
}

/**
 * Where a content is, as one bar of five segments: the stages behind it
 * filled, the one it is in filled as far as it has got, the rest empty.
 *
 * The list draws it from the content alone. The content page passes what it
 * already polls — whether the plan parses, the production view, the automatic
 * review rounds — so the active segment can say more than the list can.
 *
 * A failed content marks the stage it failed at rather than adding a sixth
 * segment, because "failed" is not a stage of its own — it is a stage that did
 * not finish.
 */
export function StageProgress({
  content,
  planParses = null,
  production = null,
  auto = null,
  compact = false,
  label = 'Progress',
}: {
  readonly content: Content;
  /** From the planning view; `null` reads "a plan exists" off the step count. */
  readonly planParses?: boolean | null;
  readonly production?: Production | null;
  readonly auto?: AutoReviewView | null;
  /** Only the bar, sized for a list row. */
  readonly compact?: boolean;
  readonly label?: string;
}) {
  const segments = segmentsOf(content, planParses, production, auto);
  return (
    <div className={`stage-progress${compact ? ' stage-progress--compact' : ''}`}>
      <div className="stage-progress__bar" role="group" aria-label={label}>
        {segments.map((segment) => (
          <div
            key={segment.stage}
            className={segmentClass(segment)}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={segment.percent}
            aria-label={`${segment.label}: ${segment.detail}`}
            title={compact ? `${segment.label}: ${segment.detail}` : undefined}
          >
            <div
              className="stage-progress__fill"
              style={{ width: `${String(segment.percent)}%` }}
            />
          </div>
        ))}
      </div>
      {!compact && (
        <ol className="stage-progress__labels">
          {segments.map((segment) => (
            <li key={segment.stage} className={segmentClass(segment, 'stage-progress__stage')}>
              <span className="stage-progress__name">
                {segment.label}
                {segment.needsYou && <span className="stage-progress__flag">needs you</span>}
              </span>
              <span className="stage-progress__detail">{segment.detail}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function segmentClass(segment: Segment, base = 'stage-progress__segment'): string {
  return `${base} ${base}--${segment.state}${segment.needsYou ? ` ${base}--needs-you` : ''}`;
}

function segmentsOf(
  content: Content,
  planParses: boolean | null,
  production: Production | null,
  auto: AutoReviewView | null,
): Segment[] {
  const failedAt: StageKey | null = content.status === 'failed' ? failureStage(content) : null;
  // An automatic round produces the next version, so the content reads
  // `producing` while it runs; the operator is still looking at a review.
  const reviewRunning = (auto?.running ?? false) && content.status !== 'delivered';
  const current: StageKey = failedAt ?? (reviewRunning ? 'review' : stageOf(content.status));
  const index = STAGE_ORDER.indexOf(current);
  const hasPlan = planParses ?? content.steps.total > 0;
  const steps = production === null ? content.steps : productionSteps(production);

  const plural = (count: number, word: string): string =>
    `${String(count)} ${count === 1 ? word : `${word}s`}`;

  // The fill and the line under the segment, for the stage the content is in.
  const active: Record<StageKey, { percent: number; detail: string; needsYou: boolean }> = {
    plan: {
      percent: hasPlan ? 100 : 0,
      detail: content.scheduleMissed
        ? 'missed its start'
        : hasPlan
          ? wording.ATTENTION_PLAN
          : planParses === false && content.steps.total > 0
            ? 'plan needs fixing'
            : 'no plan yet',
      needsYou: content.status === 'pending' && (hasPlan || content.scheduleMissed),
    },
    ready:
      content.queuePosition !== null
        ? { percent: 0, detail: `waiting for its turn (#${String(content.queuePosition)})`, needsYou: false }
        : content.scheduledStartAt !== null && content.status === 'ready'
          ? { percent: 0, detail: startsIn(content.scheduledStartAt), needsYou: false }
          : { percent: 100, detail: 'ready to start', needsYou: false },
    produce: {
      percent: fraction(steps.done, steps.total),
      detail:
        content.status === 'waiting'
          ? content.waitingUntil === null
            ? 'paused'
            : `paused until ${hourMinute(content.waitingUntil)}`
          : content.status === 'approval'
            ? content.gate === 'sketch'
              ? wording.ATTENTION_SCENES
              : wording.ATTENTION_NARRATION
            : `${String(steps.done)} of ${plural(steps.total, 'step')}`,
      needsYou: content.status === 'approval',
    },
    review:
      reviewRunning && auto !== null
        ? {
            percent: fraction(auto.rounds.length, auto.limit),
            detail: `checking, round ${String(Math.max(auto.round, 1))} of ${String(auto.limit)}`,
            needsYou: false,
          }
        : { percent: 100, detail: versionLine(content), needsYou: content.status === 'review' },
    delivered: {
      percent: 100,
      detail: content.approvedAt === null ? 'delivered' : localTime(content.approvedAt),
      needsYou: false,
    },
  };

  // What a stage behind or ahead of the content says about itself.
  const settled: Record<StageKey, string> = {
    plan: content.steps.total === 0 ? 'no plan yet' : plural(content.steps.total, 'step'),
    ready: content.scheduledStartAt === null ? '' : localTime(content.scheduledStartAt),
    produce: content.steps.total === 0 ? '' : `${String(steps.done)} of ${plural(steps.total, 'step')}`,
    review: versionLine(content),
    delivered: content.approvedAt === null ? '' : localTime(content.approvedAt),
  };

  return STAGE_ORDER.map((stage, position): Segment => {
    const label = STAGE_LABEL[stage];
    if (stage === failedAt) {
      return {
        stage,
        label,
        state: 'failed',
        percent: stage === 'produce' ? fraction(steps.done, steps.total) : 100,
        detail: wording.attentionFailed(content.failureStage),
        needsYou: true,
      };
    }
    if (position < index) {
      return { stage, label, state: 'done', percent: 100, detail: settled[stage] || 'done', needsYou: false };
    }
    if (position === index) {
      const { percent, detail, needsYou } = active[stage];
      return {
        stage,
        label,
        state: stage === 'delivered' ? 'done' : 'current',
        percent,
        detail,
        needsYou,
      };
    }
    return { stage, label, state: 'todo', percent: 0, detail: 'not yet', needsYou: false };
  });
}

function productionSteps(production: Production): { done: number; total: number } {
  return {
    done: production.steps.filter((step) => step.status === 'done').length,
    total: production.steps.length,
  };
}

function versionLine(content: Content): string {
  return content.versionCount === 0
    ? 'no version yet'
    : `version ${String(content.currentVersion)} of ${String(content.versionCount)}`;
}

function fraction(done: number, total: number): number {
  return total <= 0 ? 0 : Math.min(100, Math.round((done / total) * 100));
}

function stageOf(status: ContentStatus): StageKey {
  switch (status) {
    case 'pending':
      return 'plan';
    case 'ready':
      return 'ready';
    case 'producing':
    case 'waiting':
    case 'approval':
    case 'failed':
      return 'produce';
    case 'review':
      return 'review';
    // A rejected content went the whole way; the rejection came after.
    case 'delivered':
    case 'rejected':
      return 'delivered';
  }
}

/** Which stage a failure belongs to, read off the stage the server recorded. */
function failureStage(content: Content): StageKey {
  switch (content.failureStage) {
    case 'setup':
      return 'plan';
    case 'plan':
      return 'ready';
    case 'review':
      return 'review';
    default:
      return 'produce';
  }
}
